import React, { useEffect, useRef, useState } from "react";
import { getStaffingColor, formatDeficit, formatNumber } from "./utils";

function PlanEditableCell({ department, isSaving, onSave }) {
  const [isEditing, setIsEditing] = useState(false);
  const [draftValue, setDraftValue] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const openEditor = () => {
    if (isSaving) return;
    setDraftValue(department.plan != null ? String(department.plan) : "");
    setIsEditing(true);
  };

  const closeEditor = () => {
    setIsEditing(false);
  };

  const handleSave = async () => {
    const trimmed = draftValue.trim();
    if (!trimmed) {
      closeEditor();
      return;
    }

    const nextPlan = Number(trimmed);
    if (!Number.isInteger(nextPlan) || nextPlan < 0) {
      return;
    }

    if (department.plan === nextPlan) {
      closeEditor();
      return;
    }

    const isSaved = await onSave(department.id, nextPlan);
    if (isSaved) {
      closeEditor();
    }
  };

  if (isEditing) {
    return (
      <div className="hr-dashboard__plan-editor">
        <input
          ref={inputRef}
          type="number"
          min="0"
          step="1"
          className="hr-dashboard__plan-input"
          value={draftValue}
          disabled={isSaving}
          onChange={(event) => setDraftValue(event.target.value)}
          onBlur={handleSave}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              handleSave();
            }
            if (event.key === "Escape") {
              event.preventDefault();
              closeEditor();
            }
          }}
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      className="hr-dashboard__plan-button"
      onClick={openEditor}
      disabled={isSaving}
      title="Нажмите, чтобы указать план"
    >
      {isSaving ? "…" : formatNumber(department.plan)}
    </button>
  );
}

function StaffingTable({ departments, totals, onPlanSave, planSaveError }) {
  const [savingDepartmentId, setSavingDepartmentId] = useState(null);

  const renderStaffingCell = (percent) => {
    if (percent == null) {
      return <span className="hr-dashboard__empty-value">—</span>;
    }

    const color = getStaffingColor(percent);
    return (
      <div className="hr-dashboard__staffing-cell">
        <span>{percent}%</span>
        <div className="hr-dashboard__progress">
          <div
            className={`hr-dashboard__progress-fill hr-dashboard__progress-fill--${color}`}
            style={{ width: `${Math.min(percent, 100)}%` }}
          />
        </div>
      </div>
    );
  };

  const handlePlanSave = async (departmentId, plan) => {
    if (!onPlanSave) return false;

    setSavingDepartmentId(departmentId);
    try {
      await onPlanSave(departmentId, plan);
      return true;
    } catch {
      return false;
    } finally {
      setSavingDepartmentId(null);
    }
  };

  return (
    <section className="hr-dashboard__card">
      <h2 className="hr-dashboard__section-title">Укомплектованность по отделениям</h2>
      {onPlanSave && (
        <p className="hr-dashboard__hint hr-dashboard__hint--inline">
          Нажмите на значение в колонке «План», чтобы указать штатную численность.
        </p>
      )}
      {planSaveError && (
        <div className="hr-dashboard__inline-error">{planSaveError}</div>
      )}
      {departments.length === 0 ? (
        <p className="hr-dashboard__empty-state">Нет данных по отделениям</p>
      ) : (
        <div className="hr-dashboard__table-wrap">
          <table className="hr-dashboard__table">
            <thead>
              <tr>
                <th>Отделение</th>
                <th>План</th>
                <th>Факт</th>
                <th>Дефицит</th>
                <th>Укомплектованность</th>
              </tr>
            </thead>
            <tbody>
              {departments.map((dept) => (
                <tr key={dept.id || dept.code}>
                  <td>
                    <div className="hr-dashboard__dept-cell">
                      <span className="hr-dashboard__dept-code">{dept.code}</span>
                      <span className="hr-dashboard__dept-name">{dept.name}</span>
                    </div>
                  </td>
                  <td>
                    {onPlanSave ? (
                      <PlanEditableCell
                        department={dept}
                        isSaving={savingDepartmentId === dept.id}
                        onSave={handlePlanSave}
                      />
                    ) : (
                      formatNumber(dept.plan)
                    )}
                  </td>
                  <td>{dept.fact}</td>
                  <td
                    className={
                      dept.deficit != null && dept.deficit < 0
                        ? "hr-dashboard__deficit"
                        : ""
                    }
                  >
                    {dept.deficit != null ? formatDeficit(dept.deficit) : "—"}
                  </td>
                  <td>{renderStaffingCell(dept.staffingPercent)}</td>
                </tr>
              ))}
              <tr className="hr-dashboard__table-total">
                <td>Итого</td>
                <td>{formatNumber(totals.plan)}</td>
                <td>{totals.fact}</td>
                <td
                  className={
                    totals.deficit != null && totals.deficit < 0
                      ? "hr-dashboard__deficit"
                      : ""
                  }
                >
                  {totals.deficit != null ? formatDeficit(totals.deficit) : "—"}
                </td>
                <td>{renderStaffingCell(totals.staffingPercent)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      {!totals.hasPlan && (
        <p className="hr-dashboard__hint">
          Штатный план не заполнен — укомплектованность и дефицит недоступны.
        </p>
      )}
    </section>
  );
}

export default StaffingTable;
